<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { array, date, object, string } from 'yup';

const { tenant, websiteVariants, users, customerAudit } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
    users: {
        type: Array,
        required: true,
    },
    customerAudit: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const form = reactive({
    websiteVariant: websiteVariants.find(
        ({ id }) => id === customerAudit?.websiteVariantId,
    ),
    name: customerAudit ? customerAudit.name : null,
    notes: customerAudit ? customerAudit.notes : null,
    date: customerAudit ? customerAudit.date : null,
    users: customerAudit
        ? users.filter((user) => customerAudit.userIds.includes(user.id))
        : [],
});

const validationSchema = object({
    websiteVariant: object().required('Site requis'),
    name: string().nullable(),
    notes: string().nullable(),
    date: date().nullable(),
    users: array()
        .min(1, 'Au moins un utilisateur est requis')
        .required('Utilisateurs requis'),
});

const isSubmitting = ref(false);

function submit() {
    axios
        .post(
            route('front.projectManagement.sem.postCustomerAudit', {
                tenant: tenant.id,
                ...(customerAudit ? { customerAudit: customerAudit.id } : {}),
            }),
            {
                websiteVariantId: form.websiteVariant.id,
                name: form.name,
                notes: form.notes,
                date: form.date,
                userIds: form.users.map(({ id }) => id),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.sem.customerAudits', {
                    tenant: tenant.id,
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.sem.customerAudits', {
                    tenant: tenant.id,
                }),
            );
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <ComboboxField
                    v-model="form.websiteVariant"
                    label="Site"
                    field-name="websiteVariant"
                    field-id="websiteVariant"
                    :options="websiteVariants"
                    filtered-option-key="name"
                    required
                >
                    <template #optionContent="{ option }">
                        {{ option.name }}
                    </template>
                </ComboboxField>

                <ComboboxField
                    v-model="form.users"
                    :options="users"
                    label="Utilisateurs"
                    field-name="users"
                    field-id="users"
                    filteredOptionKey="fullName"
                    multiple
                    required
                >
                    <template #optionContent="{ option }">
                        {{ option.fullName }}
                    </template>
                </ComboboxField>

                <InputField
                    v-model="form.date"
                    label="Date"
                    field-name="date"
                    field-id="date"
                    type="date"
                />
            </FormContent>

            <FormContent>
                <InputField
                    v-model="form.name"
                    label="Nom"
                    field-name="name"
                    field-id="name"
                />

                <EditorField
                    v-model="form.notes"
                    label="Notes"
                    field-name="notes"
                    field-id="notes"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="customerAudit"
                    :url="
                        route(
                            'front.projectManagement.sem.deleteCustomerAudit',
                            {
                                tenant: tenant.id,
                                customerAudit: customerAudit.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.sem.customerAudits',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
